import React, { useEffect, useMemo, useState } from 'react';

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const AUTH_RETRY_DELAYS_MS = [400, 1200];

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function fetchAuthStatus(forceDiscord = false) {
  const suffix = forceDiscord ? '?refresh=1' : '';
  let lastError = null;

  for (let attempt = 0; attempt <= AUTH_RETRY_DELAYS_MS.length; attempt += 1) {
    if (attempt > 0) {
      await wait(AUTH_RETRY_DELAYS_MS[attempt - 1]);
    }

    try {
      const response = await fetch(API_BASE + '/auth/me' + suffix, {
        cache: 'no-store',
        credentials: 'include',
      });
      const payload = await response.json().catch(() => ({}));

      const transientFailure = response.status === 429 || response.status >= 500;
      if (!transientFailure || attempt === AUTH_RETRY_DELAYS_MS.length) {
        return { response, payload };
      }

      lastError = new Error(
        payload.message || 'Authentication service is temporarily unavailable.'
      );
    } catch (error) {
      lastError = error;
      if (attempt === AUTH_RETRY_DELAYS_MS.length) {
        throw error;
      }
    }
  }

  throw lastError || new Error('Could not verify Discord access.');
}

const QUALITY_ORDER = {
  WEAK: 0,
  MEDIUM: 1,
  GOOD: 2,
  BEST: 3,
};

const BASIC_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'ready', label: 'Ready' },
  { key: 'aligned', label: 'Aligned' },
];

const CATEGORY_OPTIONS = [
  { key: 'FOREX_BASIC', label: 'Forex Basic' },
  { key: 'FOREX_EXOTIC', label: 'Forex Exotic' },
  { key: 'INDICES', label: 'Indices' },
  { key: 'STOCKS', label: 'Stocks' },
  { key: 'METALS', label: 'Metals' },
  { key: 'ENERGY', label: 'Energy' },
  { key: 'COMMODITIES', label: 'Commodities' },
  { key: 'CRYPTO', label: 'Crypto' },
  { key: 'OTHER', label: 'Other' },
];

const ALL_CATEGORY_KEYS = CATEGORY_OPTIONS.map((item) => item.key);

const MODEL_DISPLAY_ORDER = [
  '1W/1D/1H',
  'D/4H/30m/5m',
  'D/2H/15m',
  'D/1H/5m/1m',
];

const STOCK_SORT_OPTIONS = [
  { key: 'PRICE', label: 'Price', defaultDirection: 'desc' },
  { key: '1D', label: '1D', defaultDirection: 'desc' },
  { key: '1W', label: '1W', defaultDirection: 'desc' },
  { key: '1M', label: '1M', defaultDirection: 'desc' },
  { key: 'RANGE30', label: '30D Range', defaultDirection: 'desc' },
  { key: 'ADR20', label: 'ADR(20)', defaultDirection: 'desc' },
  { key: '2H', label: '2H', defaultDirection: 'desc' },
  { key: '8H', label: '8H', defaultDirection: 'desc' },
  { key: 'SPREAD_ATR', label: 'Spread / ATR', defaultDirection: 'asc' },
];

function stockSortOption(key) {
  return STOCK_SORT_OPTIONS.find((option) => option.key === key) || null;
}


function qualityRank(value) {
  return QUALITY_ORDER[String(value || '').toUpperCase()] ?? -1;
}

function qualityClassFromScore(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  if (number >= 80) return 'BEST';
  if (number >= 70) return 'GOOD';
  if (number >= 60) return 'MEDIUM';
  return 'WEAK';
}

function getTimeframeMetrics(record, key) {
  const expanded = record?.timeframeMetrics?.[key] || null;
  const legacy = record?.[key] || null;
  const source = expanded || legacy;

  if (!source) return null;

  const quality = source?.quality ?? legacy?.quality ?? null;
  const qualityClass =
    source?.qualityClass ??
    legacy?.qualityClass ??
    qualityClassFromScore(quality);

  return {
    timeframe: source?.timeframe ?? legacy?.timeframe ?? '',
    direction: source?.direction ?? legacy?.direction ?? '',
    phase: source?.phase ?? legacy?.phase ?? '',
    quality,
    qualityClass,
    qualityPass: source?.qualityPass ?? legacy?.qualityPass ?? null,
    leg:
      source?.leg ??
      source?.legCount ??
      legacy?.leg ??
      legacy?.legCount ??
      null,
  };
}


function cleanStockCompanyName(value) {
  let name = String(value || '').trim();
  if (!name) return '';

  // Brokers often append product wording to the underlying company name.
  // Keep only what students need to identify the stock on the chart.
  name = name
    .replace(/\s*[,|\-]?\s*(spot|cash)\s+cfd\s*$/i, '')
    .replace(/\s*[,|\-]?\s*(stock|share)\s+cfd\s*$/i, '')
    .replace(/\s*\(cfd\)\s*$/i, '')
    .replace(/\s*,\s*cfd\s*$/i, '')
    .trim();

  return name;
}

function getInstrumentIdentity(record) {
  // marketCode comes from the broker's own MT5 stock folder (NAS, NYS, LSE,
  // ETR, ASX, ...). Intentionally DO NOT fall back to SYMBOL_EXCHANGE-derived
  // values such as XNGS/XNYS because those are MIC codes and do not match the
  // market folders students see in the broker terminal.
  const symbolPath = String(record?.symbolPath || '').trim();
  const pathParts = symbolPath.split(/[\\/]+/).filter(Boolean);
  const derivedMarketCode =
    pathParts.length >= 2 ? pathParts[pathParts.length - 2] : '';

  const marketCode = String(record?.marketCode || derivedMarketCode || '').trim();
  const fullName = cleanStockCompanyName(
    record?.fullName || record?.description || ''
  );

  return { fullName, marketCode };
}

function instrumentSearchText(record) {
  const identity = getInstrumentIdentity(record);
  return [record?.symbol, identity.fullName, identity.marketCode]
    .filter(Boolean)
    .join(' ')
    .toUpperCase();
}

function InstrumentSymbolCell({ record }) {
  const identity = getInstrumentIdentity(record);
  const isStock = record?.category === 'STOCKS';
  const price = record?.price?.bid ?? record?.price?.last ?? '—';

  if (!isStock) {
    return (
      <div className="symbol-cell">
        <strong>{record.symbol}</strong>
        <span className="symbol-price">{price}</span>
      </div>
    );
  }

  return (
    <div className="symbol-cell stock-symbol-cell">
      <div className="symbol-title-row">
        <strong>{record.symbol}</strong>
        {identity.marketCode && (
          <span className="exchange-badge">{identity.marketCode}</span>
        )}
      </div>
      {identity.fullName && (
        <span className="symbol-company-name">{identity.fullName}</span>
      )}
      <span className="symbol-price">{price}</span>
    </div>
  );
}

function passesQualityFilter(record, key, minimum) {
  if (minimum === 'ANY') return true;

  const metric = getTimeframeMetrics(record, key);
  if (!metric) return false;

  return qualityRank(metric.qualityClass) >= qualityRank(minimum);
}

function passesMaxLegFilter(record, key, maximumText) {
  if (maximumText === '') return true;

  const maximum = Number(maximumText);
  const metric = getTimeframeMetrics(record, key);
  const leg = Number(metric?.leg);

  return Number.isFinite(maximum) && Number.isFinite(leg) && leg <= maximum;
}

function directionClass(directionCode) {
  if (directionCode === 1) return 'direction bullish';
  if (directionCode === -1) return 'direction bearish';
  return 'direction neutral';
}

function phaseBadgeClass(phase) {
  const value = String(phase || '').toUpperCase();

  if (value.includes('PULLBACK')) return 'badge badge-blue';
  if (value.includes('EXPANSION')) return 'badge badge-green';
  if (value.includes('INVALID')) return 'badge badge-red';
  return 'badge badge-gray';
}

function formatPercent(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '—';
  }

  const number = Number(value);
  const sign = number > 0 ? '+' : '';
  return `${sign}${number.toFixed(1)}%`;
}

function formatMetric(value, digits = 2) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '—';
  }

  return Number(value).toFixed(digits);
}

function directionalMove(record, field) {
  const raw = record?.stockMetrics?.[field];

  if (raw === null || raw === undefined || !Number.isFinite(Number(raw))) {
    return null;
  }

  const value = Number(raw);
  const directionCode = Number(record?.overall?.directionCode || 0);

  if (directionCode === 1) return value;
  if (directionCode === -1) return -value;

  return null;
}


function spreadMetricsFor(record) {
  if (record?.spreadMetrics) return record.spreadMetrics;

  // Backward compatibility with schema v2 snapshots while the EA is being upgraded.
  if (record?.stockMetrics) {
    return {
      spreadPrice: record.stockMetrics.spreadPrice,
      spreadPercent: record.stockMetrics.spreadPercent,
      entryAtrTimeframe: record.stockMetrics.entryAtrTimeframe,
      entryAtrPeriod: record.stockMetrics.entryAtrPeriod,
      entryAtr: record.stockMetrics.entryAtr,
      spreadAtr: record.stockMetrics.spreadAtr,
    };
  }

  return null;
}

function spreadAtrFor(record) {
  const metrics = spreadMetricsFor(record);
  if (!metrics) return null;

  const value = Number(metrics.spreadAtr);
  return Number.isFinite(value) ? value : null;
}

function SpreadMetricsCell({ record }) {
  const metrics = spreadMetricsFor(record);

  if (!metrics) {
    return <span className="spread-metrics-na">—</span>;
  }

  const spreadAtr = spreadAtrFor(record);
  const spreadClass =
    spreadAtr === null
      ? 'neutral'
      : spreadAtr < 0.5
        ? 'good'
        : spreadAtr < 1
          ? 'warning'
          : spreadAtr < 1.5
            ? 'high'
            : spreadAtr < 2
              ? 'bad'
              : 'extreme';

  return (
    <div className="spread-metrics-cell">
      <strong className={`spread-atr ${spreadClass}`}>
        {spreadAtr === null ? '—' : `${spreadAtr.toFixed(2)} ATR`}
      </strong>
      <span>
        {metrics.entryAtrTimeframe
          ? `ATR20 ${metrics.entryAtrTimeframe}`
          : 'Entry ATR'}
      </span>
      <span>
        {Number.isFinite(Number(metrics.spreadPercent))
          ? `${Number(metrics.spreadPercent).toFixed(3)}% spread`
          : 'Spread % —'}
      </span>
    </div>
  );
}

function stockSortValue(record, key) {
  const metrics = record?.stockMetrics;

  if (!metrics) return null;

  if (key === 'PRICE') {
    const value = Number(metrics.referencePrice);
    return Number.isFinite(value) ? value : null;
  }

  if (key === '1D') return directionalMove(record, 'change1DPercent');
  if (key === '1W') return directionalMove(record, 'change1WPercent');
  if (key === '1M') return directionalMove(record, 'change1MPercent');
  if (key === '2H') return directionalMove(record, 'change2HPercent');
  if (key === '8H') return directionalMove(record, 'change8HPercent');

  if (key === 'RANGE30') {
    const value = Number(metrics.absoluteRange30Percent);
    return Number.isFinite(value) ? value : null;
  }

  if (key === 'ADR20') {
    const value = Number(metrics.adr20Percent);
    return Number.isFinite(value) ? value : null;
  }

  if (key === 'SPREAD_ATR') {
    return spreadAtrFor(record);
  }

  return null;
}

function StockMetricsCell({ record }) {
  if (record?.category !== 'STOCKS' || !record?.stockMetrics) {
    return <span className="stock-metrics-na">—</span>;
  }

  const metrics = record.stockMetrics;

  return (
    <div className="stock-metrics-cell">
      <div>
        <span>D {formatPercent(metrics.change1DPercent)}</span>
        <span>W {formatPercent(metrics.change1WPercent)}</span>
        <span>M {formatPercent(metrics.change1MPercent)}</span>
      </div>
      <div>
        <span>ADR {formatPercent(metrics.adr20Percent)}</span>
        <span>30D {formatPercent(metrics.absoluteRange30Percent)}</span>
      </div>
      <div>
        <span>2H {formatPercent(metrics.change2HPercent)}</span>
        <span>8H {formatPercent(metrics.change8HPercent)}</span>
      </div>
    </div>
  );
}


function timeframeLabel(metric, metricKey) {
  if (metric?.timeframe) return metric.timeframe;
  if (metricKey === 'middle') return 'MIDDLE';
  return metricKey === 'context1' ? 'CONTEXT 1' : 'CONTEXT 2';
}

function TimeframePhaseCell({ record, metricKey }) {
  const metric = getTimeframeMetrics(record, metricKey);

  if (!metric) {
    return <span className="timeframe-metrics-na">—</span>;
  }

  return (
    <div className={`tf-phase-cell tf-${metricKey}`}>
      <span className="tf-timeframe-label">{timeframeLabel(metric, metricKey)}</span>
      <span className={phaseBadgeClass(metric.phase)}>
        {metric.phase || '—'}
      </span>
    </div>
  );
}

function TimeframeQualityCell({ record, metricKey }) {
  const metric = getTimeframeMetrics(record, metricKey);

  if (!metric) {
    return <span className="timeframe-metrics-na">—</span>;
  }

  const rawQuality = Number(metric.quality);
  const quality = Number.isFinite(rawQuality) ? rawQuality : null;
  const qualityClass = String(metric.qualityClass || '—').toUpperCase();
  const width = quality === null ? 0 : Math.max(0, Math.min(100, quality));

  return (
    <div className={`tf-quality-cell tf-${metricKey}`}>
      <div className="tf-quality-head">
        <strong>{quality === null ? '—' : quality.toFixed(1)}</strong>
        <span className={`quality-class quality-${qualityClass.toLowerCase()}`}>
          {qualityClass}
        </span>
      </div>
      <div className="tf-quality-track" aria-hidden="true">
        <i style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function TimeframeLegCell({ record, metricKey }) {
  const metric = getTimeframeMetrics(record, metricKey);

  if (!metric) {
    return <span className="timeframe-metrics-na">—</span>;
  }

  const rawLeg = Number(metric.leg);
  const leg = Number.isFinite(rawLeg) ? rawLeg : null;

  return (
    <div className={`tf-leg-cell tf-${metricKey}`}>
      <span>{leg === null ? '—' : leg}</span>
    </div>
  );
}

function rowClass(record) {
  if (record?.overall?.ready) return 'scanner-row row-ready';
  if (record?.overall?.aligned) return 'scanner-row row-aligned';
  return 'scanner-row';
}

function StatusPill({ record }) {
  if (record?.overall?.ready) {
    return <span className="status-pill ready">READY</span>;
  }

  if (record?.overall?.aligned) {
    return <span className="status-pill aligned">ALIGNED</span>;
  }

  return <span className="status-pill inactive">WAITING</span>;
}



let tradingViewScriptPromise = null;

function loadTradingViewScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('TradingView requires a browser environment.'));
  }

  if (window.TradingView) {
    return Promise.resolve(window.TradingView);
  }

  if (tradingViewScriptPromise) {
    return tradingViewScriptPromise;
  }

  tradingViewScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-tradingview-script="true"]');

    if (existing) {
      existing.addEventListener('load', () => resolve(window.TradingView), { once: true });
      existing.addEventListener('error', () => reject(new Error('TradingView script failed to load.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/tv.js';
    script.async = true;
    script.dataset.tradingviewScript = 'true';
    script.onload = () => resolve(window.TradingView);
    script.onerror = () => reject(new Error('TradingView script failed to load.'));
    document.body.appendChild(script);
  });

  return tradingViewScriptPromise;
}

function defaultChartInterval() {
  return 'D';
}


function tradingViewTurquoiseTickerFromLse(symbol) {
  // TradingView's Turquoise symbols for LSE-listed equities generally use
  // the LSE ticker with punctuation removed and an "L" venue suffix
  // (e.g. BARC -> BARCL, SHEL -> SHELL, ABDN -> ABDNL).
  const clean = String(symbol || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

  return clean ? `${clean}L` : '';
}

function tradingViewSymbolFor(record) {
  const rawSymbol = String(record?.symbol || '').trim();
  if (!rawSymbol) return 'NASDAQ:AAPL';

  const upperSymbol = rawSymbol.toUpperCase();
  const category = String(record?.category || '').toUpperCase();
  const identity = getInstrumentIdentity(record);
  const marketCode = String(identity.marketCode || '').toUpperCase();

  if (category === 'STOCKS') {
    const exchangeMap = {
      NAS: 'NASDAQ',
      NYS: 'NYSE',
      LSE: 'LSE',
      ETR: 'XETR',
      PAR: 'EURONEXT',
      ASX: 'ASX',
      TSX: 'TSX',
      SIX: 'SIX',
      BME: 'BME',
      MIL: 'MIL',
      STO: 'OMXSTO',
      CPH: 'OMXCOP',
      HEL: 'OMXHEX',
      OSL: 'OSL',
      BRU: 'EURONEXT',
      AMS: 'EURONEXT',
    };

    if (marketCode === 'LSE') {
      const turquoiseTicker = tradingViewTurquoiseTickerFromLse(upperSymbol);
      if (turquoiseTicker) return `TURQUOISE:${turquoiseTicker}`;
    }

    const prefix = exchangeMap[marketCode];
    if (prefix) return `${prefix}:${upperSymbol}`;
    return upperSymbol;
  }

  if ((category === 'FOREX_BASIC' || category === 'FOREX_EXOTIC') && /^[A-Z]{6}$/.test(upperSymbol)) {
    return `FX:${upperSymbol}`;
  }

  if (category === 'CRYPTO' && /^[A-Z0-9]{6,12}$/.test(upperSymbol)) {
    return `BINANCE:${upperSymbol}`;
  }

  const directMap = {
    XAUUSD: 'OANDA:XAUUSD',
    XAGUSD: 'OANDA:XAGUSD',
    US30: 'OANDA:US30USD',
    NAS100: 'OANDA:NAS100USD',
    SPX500: 'OANDA:SPX500USD',
    UK100: 'OANDA:UK100GBP',
    GER40: 'CAPITALCOM:DE40',
    FRA40: 'CAPITALCOM:FR40',
    JP225: 'CAPITALCOM:JPN225',
    BTCUSD: 'BITSTAMP:BTCUSD',
    ETHUSD: 'BITSTAMP:ETHUSD',
  };

  return directMap[upperSymbol] || upperSymbol;
}

function TradingViewChartPanel({ record }) {
  const [chartError, setChartError] = useState('');
  const chartIdentity = getInstrumentIdentity(record || {});
  const containerId = useMemo(
    () => `tv-chart-${Math.random().toString(36).slice(2)}`,
    []
  );
  const tvSymbol = useMemo(() => tradingViewSymbolFor(record), [record]);
  const initialInterval = useMemo(() => defaultChartInterval(record), [record]);

  useEffect(() => {
    let cancelled = false;
    let widget = null;

    setChartError('');

    loadTradingViewScript()
      .then((TradingView) => {
        if (cancelled) return;

        const container = document.getElementById(containerId);
        if (!container) return;

        container.innerHTML = '';

        widget = new TradingView.widget({
          autosize: true,
          symbol: tvSymbol,
          interval: initialInterval,
          timezone: 'Etc/UTC',
          theme: 'light',
          style: '1',
          locale: 'en',
          withdateranges: true,
          hide_side_toolbar: false,
          hide_top_toolbar: false,
          allow_symbol_change: true,
          save_image: false,
          enable_publishing: false,
          details: false,
          hotlist: false,
          calendar: false,
          hide_volume: true,
          disabled_features: [
            'create_volume_indicator_by_default',
            'use_localstorage_for_settings',
            'save_chart_properties_to_local_storage',
          ],
          // Free Advanced Chart widget: preload four EMA instances directly.
          // Moving Average Multiple is part of the full Advanced Charts library,
          // but it is not in the free widget's supported tv-basicstudies list.
          // Give each EMA a unique uid and target that uid in studies_overrides.
          // If a TradingView build ignores uid styling, the EMAs still remain
          // visible with their correct periods instead of disappearing entirely.
          studies: [
            {
              id: 'MAExp@tv-basicstudies',
              uid: 'paiEma5',
              version: 60,
              inputs: { length: 5 },
            },
            {
              id: 'MAExp@tv-basicstudies',
              uid: 'paiEma10',
              version: 60,
              inputs: { length: 10 },
            },
            {
              id: 'MAExp@tv-basicstudies',
              uid: 'paiEma20',
              version: 60,
              inputs: { length: 20 },
            },
            {
              id: 'MAExp@tv-basicstudies',
              uid: 'paiEma50',
              version: 60,
              inputs: { length: 50 },
            },
          ],
          studies_overrides: {
            // Target the individual study uids rather than the EMA study type.
            // A type-level color override would intentionally recolor ALL four
            // duplicate EMA instances to the same color.
            'paiEma5.plot.color': '#00BCD4',
            'paiEma5.ma.color': '#00BCD4',
            'paiEma5.plot.linewidth': 1,
            'paiEma5.ma.linewidth': 1,

            'paiEma10.plot.color': '#2962FF',
            'paiEma10.ma.color': '#2962FF',
            'paiEma10.plot.linewidth': 1,
            'paiEma10.ma.linewidth': 1,

            'paiEma20.plot.color': '#424242',
            'paiEma20.ma.color': '#424242',
            'paiEma20.plot.linewidth': 1,
            'paiEma20.ma.linewidth': 1,

            'paiEma50.plot.color': '#FF334E',
            'paiEma50.ma.color': '#FF334E',
            'paiEma50.plot.linewidth': 1,
            'paiEma50.ma.linewidth': 1,
          },

          // These are the configuration keys used by the FREE Advanced Chart
          // widget itself. The previous paneProperties grid overrides belong to
          // the full Charting Library API and were being ignored by tv.js.
          backgroundColor: '#ffffff',
          gridColor: 'rgba(0, 0, 0, 0)',
          container_id: containerId,
          settings_overrides: {
            'paneProperties.background': '#ffffff',
            'paneProperties.backgroundType': 'solid',
            'paneProperties.gridLinesMode': 'none',
            'paneProperties.vertGridProperties.color': 'rgba(255,255,255,0)',
            'paneProperties.horzGridProperties.color': 'rgba(255,255,255,0)',
            'scalesProperties.textColor': '#000000',
            'mainSeriesProperties.showPriceLine': true,
            'mainSeriesProperties.priceLineColor': '#000000',
          },
          overrides: {
            'paneProperties.background': '#ffffff',
            'paneProperties.backgroundType': 'solid',
            'paneProperties.gridLinesMode': 'none',
            'paneProperties.vertGridProperties.color': 'rgba(255,255,255,0)',
            'paneProperties.horzGridProperties.color': 'rgba(255,255,255,0)',
            'paneProperties.crossHairProperties.color': '#000000',
            'scalesProperties.textColor': '#000000',
            'scalesProperties.lineColor': '#d9dde3',
            'mainSeriesProperties.style': 1,
            'mainSeriesProperties.showPriceLine': true,
            'mainSeriesProperties.priceLineColor': '#000000',
            'mainSeriesProperties.candleStyle.upColor': '#ffffff',
            'mainSeriesProperties.candleStyle.downColor': '#434651',
            'mainSeriesProperties.candleStyle.borderUpColor': '#000000',
            'mainSeriesProperties.candleStyle.borderDownColor': '#000000',
            'mainSeriesProperties.candleStyle.wickUpColor': '#000000',
            'mainSeriesProperties.candleStyle.wickDownColor': '#000000',
            'mainSeriesProperties.candleStyle.drawBorder': true,
            'mainSeriesProperties.candleStyle.drawWick': true,
            'mainSeriesProperties.candleStyle.transparency': 15,
          },
        });


      })
      .catch((error) => {
        if (!cancelled) {
          setChartError(error?.message || 'TradingView chart could not be loaded.');
        }
      });

    return () => {
      cancelled = true;
      try {
        if (widget && typeof widget.remove === 'function') {
          widget.remove();
        }
      } catch (error) {
        console.warn('TradingView cleanup warning:', error);
      }

      const container = document.getElementById(containerId);
      if (container) container.innerHTML = '';
    };
  }, [containerId, tvSymbol, initialInterval]);

  return (
    <div className="chart-card">
      <div className="chart-card-header">
        <div>
          <span className="chart-card-eyebrow">Chart mode</span>
          <strong>
            {record?.symbol || '—'}
            {chartIdentity.marketCode ? ` · ${chartIdentity.marketCode}` : ''}
          </strong>
          <small>{chartIdentity.fullName || 'Interactive TradingView chart'}</small>
        </div>
        <div className="chart-card-meta">
          <span>{tvSymbol}</span>
          <span>TV feed</span>
        </div>
      </div>

      <div className="chart-card-body">
        {chartError ? (
          <div className="chart-placeholder error">
            <strong>Chart failed to load.</strong>
            <span>{chartError}</span>
          </div>
        ) : (
          <div id={containerId} className="tv-chart-container" />
        )}
      </div>

      <div className="chart-card-footer">
        <span>
          TradingView data can differ slightly from your broker feed. Always verify the chart before taking any trade.
        </span>
      </div>
    </div>
  );
}


function userDisplayName(user) {
  return user?.globalName || user?.username || 'Discord member';
}

function userInitial(user) {
  return userDisplayName(user).trim().charAt(0).toUpperCase() || 'P';
}

function UserAvatar({ user, className = '' }) {
  if (user?.avatarUrl) {
    return (
      <img
        className={`auth-user-avatar ${className}`.trim()}
        src={user.avatarUrl}
        alt=""
      />
    );
  }

  return (
    <span className={`auth-user-avatar auth-user-fallback ${className}`.trim()}>
      {userInitial(user)}
    </span>
  );
}

function AuthLoadingScreen() {
  return (
    <div className="auth-shell auth-loading-shell">
      <div className="auth-glow auth-glow-one" />
      <div className="auth-glow auth-glow-two" />
      <div className="auth-loading-card">
        <img
          className="auth-logo"
          src="/priceactionintel-logo.png"
          alt="Price Action Intel"
        />
        <div className="auth-spinner" />
        <strong>Checking scanner access…</strong>
        <span>Verifying your PriceActionIntel session.</span>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin, error }) {
  return (
    <div className="auth-shell">
      <div className="auth-grid-pattern" />
      <div className="auth-glow auth-glow-one" />
      <div className="auth-glow auth-glow-two" />

      <div className="auth-card">
        <div className="auth-brand-row">
          <img
            className="auth-logo"
            src="/priceactionintel-logo.png"
            alt="Price Action Intel"
          />
          <span className="auth-private-badge">PRIVATE ACCESS</span>
        </div>

        <div className="auth-kicker">PRICE ACTION INTEL · PAI TOOLS</div>
        <h1>Momentum Scanner</h1>
        <p className="auth-lead">
          Live multi-asset momentum intelligence for active PriceActionIntel
          members.
        </p>

        {error && (
          <div className="auth-message auth-message-error">
            <strong>Could not verify access.</strong>
            <span>{error}</span>
          </div>
        )}

        <button className="discord-login-button" onClick={onLogin}>
          <span className="discord-mark">D</span>
          <span>Continue with Discord</span>
          <span className="discord-arrow">→</span>
        </button>

        <div className="auth-access-grid">
          <div>
            <span className="auth-access-icon">✓</span>
            <div>
              <strong>PAI Tools</strong>
              <small>Active subscriber access</small>
            </div>
          </div>
          <div>
            <span className="auth-access-icon">✓</span>
            <div>
              <strong>Mentorship</strong>
              <small>Included member access</small>
            </div>
          </div>
        </div>

        <div className="auth-footnote">
          <span className="auth-lock">◆</span>
          Your Discord role is checked securely before scanner data is released.
        </div>
      </div>
    </div>
  );
}

function AccessDeniedScreen({ user, onRetry, onLogout, busy }) {
  return (
    <div className="auth-shell">
      <div className="auth-grid-pattern" />
      <div className="auth-glow auth-glow-one" />
      <div className="auth-glow auth-glow-two" />

      <div className="auth-card auth-denied-card">
        <div className="auth-brand-row">
          <img
            className="auth-logo"
            src="/priceactionintel-logo.png"
            alt="Price Action Intel"
          />
          <span className="auth-private-badge">MEMBERS AREA</span>
        </div>

        <div className="auth-status-icon denied">×</div>
        <div className="auth-kicker">DISCORD CONNECTED</div>
        <h1>Scanner access is not active</h1>
        <p className="auth-lead">
          This Discord account is valid, but it does not currently have a PAI
          Tools or Mentorship access role.
        </p>

        <div className="auth-account-card">
          <UserAvatar user={user} />
          <div>
            <strong>{userDisplayName(user)}</strong>
            <span>@{user?.username || 'discord'}</span>
          </div>
          <span className="auth-account-state">NO ACCESS</span>
        </div>

        <div className="auth-message">
          <strong>Just subscribed?</strong>
          <span>
            Once LaunchPass updates your Discord role, press “Check access again”.
          </span>
        </div>

        <div className="auth-denied-actions">
          <button className="auth-primary-button" onClick={onRetry} disabled={busy}>
            {busy ? 'Checking…' : 'Check access again'}
          </button>
          <button className="auth-secondary-button" onClick={onLogout} disabled={busy}>
            Use another Discord account
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [auth, setAuth] = useState({
    loading: true,
    authenticated: false,
    authorized: false,
    user: null,
    accessSource: null,
    error: '',
  });
  const [authBusy, setAuthBusy] = useState(false);

  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');

  const [basicFilter, setBasicFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(
    ALL_CATEGORY_KEYS
  );
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [model, setModel] = useState('ALL');
  const [direction, setDirection] = useState('ALL');
  const [context1MinQuality, setContext1MinQuality] = useState('ANY');
  const [context1MaxLeg, setContext1MaxLeg] = useState('');
  const [context2MinQuality, setContext2MinQuality] = useState('ANY');
  const [context2MaxLeg, setContext2MaxLeg] = useState('');
  const [middleMinQuality, setMiddleMinQuality] = useState('ANY');
  const [middleMaxLeg, setMiddleMaxLeg] = useState('');
  const [maxSpreadAtr, setMaxSpreadAtr] = useState('ANY');

  const [stockExchange, setStockExchange] = useState('ALL');
  const [stockPriceFilter, setStockPriceFilter] = useState('ANY');
  const [stockCustomMinPrice, setStockCustomMinPrice] = useState('');
  const [stockMin1D, setStockMin1D] = useState('');
  const [stockMin1W, setStockMin1W] = useState('');
  const [stockMin1M, setStockMin1M] = useState('');
  const [stockMinRange30, setStockMinRange30] = useState('');
  const [stockMinAdr20, setStockMinAdr20] = useState('');
  const [stockMin2H, setStockMin2H] = useState('');
  const [stockMin8H, setStockMin8H] = useState('');
  const [stockSortKey, setStockSortKey] = useState('DEFAULT');
  const [stockSortDirection, setStockSortDirection] = useState('desc');
  const [chartMode, setChartMode] = useState(false);
  const [selectedChartRecordId, setSelectedChartRecordId] = useState(null);

async function checkAuth(forceDiscord = false) {
  setAuthBusy(true);

  try {
    const { response, payload } = await fetchAuthStatus(forceDiscord);

    if (response.status === 401) {
      setAuth({
        loading: false,
        authenticated: false,
        authorized: false,
        user: null,
        accessSource: null,
        error: '',
      });
      return;
    }

    if (!response.ok) {
      throw new Error(payload.message || 'Authentication service is unavailable.');
    }

    setAuth({
      loading: false,
      authenticated: Boolean(payload.authenticated),
      authorized: Boolean(payload.authorized),
      user: payload.user || null,
      accessSource: payload.accessSource || null,
      error: '',
    });
  } catch (error) {
    setAuth({
      loading: false,
      authenticated: false,
      authorized: false,
      user: null,
      accessSource: null,
      error: error.message || 'Could not verify Discord access.',
    });
  } finally {
    setAuthBusy(false);
  }
}

function loginWithDiscord() {
  window.location.assign(API_BASE + '/auth/discord');
}

async function logout() {
  setAuthBusy(true);

  try {
    await fetch(API_BASE + '/auth/logout', {
      method: 'POST',
      cache: 'no-store',
      credentials: 'include',
    });
  } catch {
    // Even if the network request fails, clear the local UI state.
  }

  setData(null);
  setStatus(null);
  setApiError('');
  setLoading(true);
  setAuth({
    loading: false,
    authenticated: false,
    authorized: false,
    user: null,
    accessSource: null,
    error: '',
  });
  setAuthBusy(false);
}

async function load() {
  if (!auth.authorized) return;

  try {
    const response = await fetch(API_BASE + '/scanner', {
      cache: 'no-store',
      credentials: 'include',
    });

    const snapshot = await response.json().catch(() => ({}));

    if (response.status === 401 || response.status === 403) {
      await checkAuth();
      return;
    }

    if (!response.ok) {
      throw new Error(
        snapshot.message
          ? snapshot.message
          : 'Scanner snapshot is not available yet.'
      );
    }

    const generatedAt = Number(
      snapshot && snapshot.generatedAt ? snapshot.generatedAt : 0
    ) * 1000;

    setStatus({
      ok: true,
      snapshotAgeSeconds: generatedAt
        ? Math.max(0, Math.round((Date.now() - generatedAt) / 1000))
        : null,
    });

    setData({ snapshot });
    setApiError('');
  } catch (error) {
    setApiError(error.message);
    setStatus({
      ok: false,
      snapshotAgeSeconds: null,
    });
  } finally {
    setLoading(false);
  }
}

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authErrorCode = params.get('auth_error');

    if (authErrorCode) {
      const readableError =
        authErrorCode === 'cancelled'
          ? 'Discord login was cancelled.'
          : authErrorCode === 'state'
            ? 'The login session expired. Please try again.'
            : 'Discord login could not be completed. Please try again.';

      setAuth((current) => ({ ...current, error: readableError }));
      window.history.replaceState({}, '', window.location.pathname);
    }

    checkAuth();
  }, []);

  useEffect(() => {
    if (!auth.authorized) return undefined;

    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [auth.authorized]);

  const records = useMemo(() => {
    return Array.isArray(data?.snapshot?.records)
      ? data.snapshot.records.filter((record) => record?.ok !== false)
      : [];
  }, [data]);

  // Context 2 only exists in the four-timeframe models. Keep it visible in
  // All Models because the table can contain a mix of one- and two-context rows.
  // For a selected one-context model, remove the entire C2 block so the table
  // retracts instead of reserving three empty columns.
  const showContext2 = useMemo(() => {
    if (model === 'ALL') return true;

    return records.some((record) => {
      if (record?.model !== model) return false;

      const expanded = record?.timeframeMetrics?.context2;
      const legacy = record?.context2;
      return expanded !== null && expanded !== undefined
        ? true
        : legacy !== null && legacy !== undefined;
    });
  }, [records, model]);

  const stockExchangeOptions = useMemo(() => {
    const exchanges = new Map();

    records.forEach((record) => {
      if (record?.category !== 'STOCKS') return;

      const identity = getInstrumentIdentity(record);
      const label = identity.marketCode;
      if (!label) return;

      const key = label.toUpperCase();
      if (!exchanges.has(key)) exchanges.set(key, label);
    });

    return [...exchanges.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [records]);

  const stockFiltersActive =
    stockExchange !== 'ALL' ||
    stockPriceFilter !== 'ANY' ||
    stockMin1D !== '' ||
    stockMin1W !== '' ||
    stockMin1M !== '' ||
    stockMinRange30 !== '' ||
    stockMinAdr20 !== '' ||
    stockMin2H !== '' ||
    stockMin8H !== '';

  const stockSortActive = stockSortKey !== 'DEFAULT';
  const stockViewActive = stockFiltersActive || stockSortActive;

  const selectedChartRecord = useMemo(() => {
    if (!selectedChartRecordId) return null;
    return records.find((record) => record.id === selectedChartRecordId) || null;
  }, [records, selectedChartRecordId]);

  const filteredRecords = useMemo(() => {
    const searchValue = search.trim().toUpperCase();

    return records.filter((record) => {
      if (searchValue && !instrumentSearchText(record).includes(searchValue)) {
        return false;
      }

      if (basicFilter === 'ready' && !record?.overall?.ready) return false;
      if (basicFilter === 'aligned' && !record?.overall?.aligned) return false;

      const category = record?.category || 'OTHER';
      if (!selectedCategories.includes(category)) return false;

      if (model !== 'ALL' && record.model !== model) return false;

      if (
        direction !== 'ALL' &&
        String(record?.overall?.direction || '').toUpperCase() !== direction
      ) {
        return false;
      }

      if (!passesQualityFilter(record, 'context1', context1MinQuality)) {
        return false;
      }

      if (!passesMaxLegFilter(record, 'context1', context1MaxLeg)) {
        return false;
      }

      if (showContext2) {
        if (!passesQualityFilter(record, 'context2', context2MinQuality)) {
          return false;
        }

        if (!passesMaxLegFilter(record, 'context2', context2MaxLeg)) {
          return false;
        }
      }

      if (!passesQualityFilter(record, 'middle', middleMinQuality)) {
        return false;
      }

      if (!passesMaxLegFilter(record, 'middle', middleMaxLeg)) {
        return false;
      }

      if (maxSpreadAtr !== 'ANY') {
        const maximum = Number(maxSpreadAtr);
        const spreadAtr = spreadAtrFor(record);

        if (
          !Number.isFinite(maximum) ||
          spreadAtr === null ||
          spreadAtr >= maximum
        ) {
          return false;
        }
      }

      if (stockViewActive) {
        if (record?.category !== 'STOCKS' || !record?.stockMetrics) {
          return false;
        }

        const metrics = record.stockMetrics;
        const identity = getInstrumentIdentity(record);

        if (stockExchange !== 'ALL') {
          const exchangeKey = String(identity.marketCode || '').toUpperCase();

          if (exchangeKey !== stockExchange) return false;
        }

        if (!stockFiltersActive) {
          return true;
        }

        let requiredPrice = null;

        if (stockPriceFilter === '5') requiredPrice = 5;
        if (stockPriceFilter === '8') requiredPrice = 8;

        if (stockPriceFilter === 'CUSTOM') {
          const customPrice = Number(stockCustomMinPrice);
          if (stockCustomMinPrice !== '' && Number.isFinite(customPrice)) {
            requiredPrice = customPrice;
          }
        }

        if (requiredPrice !== null) {
          const referencePrice = Number(metrics.referencePrice);
          if (!Number.isFinite(referencePrice) || referencePrice < requiredPrice) {
            return false;
          }
        }

        const directionalFilters = [
          ['change1DPercent', stockMin1D],
          ['change1WPercent', stockMin1W],
          ['change1MPercent', stockMin1M],
          ['change2HPercent', stockMin2H],
          ['change8HPercent', stockMin8H],
        ];

        for (const [field, thresholdText] of directionalFilters) {
          if (thresholdText === '') continue;

          const threshold = Number(thresholdText);
          const move = directionalMove(record, field);

          if (
            !Number.isFinite(threshold) ||
            move === null ||
            move < threshold
          ) {
            return false;
          }
        }

        if (stockMinRange30 !== '') {
          const minimum = Number(stockMinRange30);
          const value = Number(metrics.absoluteRange30Percent);

          if (!Number.isFinite(minimum) || !Number.isFinite(value) || value < minimum) {
            return false;
          }
        }

        if (stockMinAdr20 !== '') {
          const minimum = Number(stockMinAdr20);
          const value = Number(metrics.adr20Percent);

          if (!Number.isFinite(minimum) || !Number.isFinite(value) || value < minimum) {
            return false;
          }
        }

      }

      return true;
    });
  }, [
    records,
    search,
    basicFilter,
    selectedCategories,
    model,
    direction,
    context1MinQuality,
    context1MaxLeg,
    context2MinQuality,
    context2MaxLeg,
    showContext2,
    middleMinQuality,
    middleMaxLeg,
    maxSpreadAtr,
    stockFiltersActive,
    stockViewActive,
    stockExchange,
    stockPriceFilter,
    stockCustomMinPrice,
    stockMin1D,
    stockMin1W,
    stockMin1M,
    stockMinRange30,
    stockMinAdr20,
    stockMin2H,
    stockMin8H,
  ]);


  const sortedRecords = useMemo(() => {
    if (!stockSortActive) {
      return filteredRecords;
    }

    const directionMultiplier = stockSortDirection === 'asc' ? 1 : -1;

    return [...filteredRecords].sort((a, b) => {
      const aValue = stockSortValue(a, stockSortKey);
      const bValue = stockSortValue(b, stockSortKey);

      const aMissing = aValue === null || !Number.isFinite(Number(aValue));
      const bMissing = bValue === null || !Number.isFinite(Number(bValue));

      if (aMissing && bMissing) {
        return String(a.symbol || '').localeCompare(String(b.symbol || ''));
      }

      if (aMissing) return 1;
      if (bMissing) return -1;

      const numericDifference =
        (Number(aValue) - Number(bValue)) * directionMultiplier;

      if (numericDifference !== 0) {
        return numericDifference;
      }

      const symbolDifference = String(a.symbol || '').localeCompare(
        String(b.symbol || '')
      );

      if (symbolDifference !== 0) {
        return symbolDifference;
      }

      return String(a.model || '').localeCompare(String(b.model || ''));
    });
  }, [
    filteredRecords,
    stockSortActive,
    stockSortKey,
    stockSortDirection,
  ]);

  const activeStockSort = stockSortOption(stockSortKey);

  function chooseStockSort(key) {
    if (key === 'DEFAULT') {
      setStockSortKey('DEFAULT');
      setStockSortDirection('desc');
      return;
    }

    const option = stockSortOption(key);
    if (!option) return;

    if (stockSortKey === key) {
      setStockSortDirection((current) =>
        current === 'desc' ? 'asc' : 'desc'
      );
      return;
    }

    setStockSortKey(key);
    setStockSortDirection(option.defaultDirection);
  }

  function stockSortArrow(key) {
    if (stockSortKey !== key) return '↕';
    return stockSortDirection === 'desc' ? '↓' : '↑';
  }

  const stockSortMeaning =
    stockSortKey === 'SPREAD_ATR'
      ? stockSortDirection === 'asc'
        ? 'lowest spread first'
        : 'highest spread first'
      : stockSortDirection === 'desc'
        ? 'strongest / highest first'
        : 'weakest / lowest first';

  const models = useMemo(() => {
    const available = [...new Set(records.map((record) => record.model).filter(Boolean))];

    return available.sort((a, b) => {
      const aIndex = MODEL_DISPLAY_ORDER.indexOf(a);
      const bIndex = MODEL_DISPLAY_ORDER.indexOf(b);

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return String(a).localeCompare(String(b));
    });
  }, [records]);

  useEffect(() => {
    if (!chartMode) return;

    if (sortedRecords.length === 0) {
      if (selectedChartRecordId !== null) setSelectedChartRecordId(null);
      return;
    }

    const selectedStillVisible = sortedRecords.some(
      (record) => record.id === selectedChartRecordId
    );

    if (!selectedStillVisible) {
      setSelectedChartRecordId(sortedRecords[0].id);
    }
  }, [chartMode, sortedRecords, selectedChartRecordId]);

  const counts = useMemo(() => {
    return {
      all: records.length,
      ready: records.filter((r) => r?.overall?.ready).length,
      aligned: records.filter((r) => r?.overall?.aligned).length,
    };
  }, [records]);

  const categoryCounts = useMemo(() => {
    return CATEGORY_OPTIONS.reduce((acc, item) => {
      acc[item.key] = records.filter(
        (record) => (record?.category || 'OTHER') === item.key
      ).length;
      return acc;
    }, {});
  }, [records]);

  function toggleCategory(key) {
    setSelectedCategories((current) => {
      if (current.includes(key)) {
        return current.filter((item) => item !== key);
      }
      return [...current, key];
    });
  }

  function selectAllCategories() {
    setSelectedCategories(ALL_CATEGORY_KEYS);
  }

  const allCategoriesSelected =
    selectedCategories.length === ALL_CATEGORY_KEYS.length;

  const snapshotAge = status?.snapshotAgeSeconds;
  const stale = snapshotAge !== null && snapshotAge > 90;

  if (auth.loading) {
    return <AuthLoadingScreen />;
  }

  if (!auth.authenticated) {
    return <LoginScreen onLogin={loginWithDiscord} error={auth.error} />;
  }

  if (!auth.authorized) {
    return (
      <AccessDeniedScreen
        user={auth.user}
        onRetry={() => checkAuth(true)}
        onLogout={logout}
        busy={authBusy}
      />
    );
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <img
            className="brand-logo"
            src="/priceactionintel-logo.png"
            alt="Price Action Intel"
          />
          <div className="brand-copy">
            <div className="brand-title">MOMENTUM SCANNER</div>
            <div className="brand-subtitle">Multi-asset momentum intelligence</div>
          </div>
        </div>

        <div className="topbar-actions">
          <button className="topbar-refresh" onClick={load}>
            ↻ <span>Refresh</span>
          </button>
          <div className={`connection ${stale ? 'stale' : ''}`}>
            <span className="connection-dot" />
            {status?.ok
              ? `Live · ${snapshotAge ?? 0}s ago`
              : 'Waiting for MT5'}
          </div>

          <div className="topbar-account">
            <UserAvatar user={auth.user} className="topbar-avatar" />
            <div className="topbar-account-copy">
              <strong>{userDisplayName(auth.user)}</strong>
              <span>{auth.accessSource || 'Member access'}</span>
            </div>
            <button
              className="topbar-logout"
              onClick={logout}
              disabled={authBusy}
              title="Log out"
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className={chartMode ? 'page chart-page-open' : 'page'}>
        <section className="hero">
          <div>
            <div className="eyebrow">PRICE ACTION INTEL · MARKET SCANNER</div>
            <h1>Find the cleanest momentum first.</h1>
            <p>
              Filter every PAI timeframe model by alignment, quality, execution
              cost and market-specific momentum metrics.
            </p>
          </div>
        </section>

        <section className="summary-grid">
          <div className="summary-card summary-total">
            <div className="summary-card-copy">
              <span>All records</span>
              <strong>{counts.all}</strong>
              <small>Current scanner universe</small>
            </div>
            <div className="summary-icon">◎</div>
          </div>

          <div className="summary-card summary-ready">
            <div className="summary-card-copy">
              <span>Ready</span>
              <strong>{counts.ready}</strong>
              <small>Search for entry</small>
            </div>
            <div className="summary-icon">✓</div>
          </div>

          <div className="summary-card summary-aligned">
            <div className="summary-card-copy">
              <span>Aligned</span>
              <strong>{counts.aligned}</strong>
              <small>PAI conditions aligned</small>
            </div>
            <div className="summary-icon">↗</div>
          </div>

          <div className="summary-card summary-models">
            <div className="summary-card-copy">
              <span>Models</span>
              <strong>{models.length}</strong>
              <small>{model === 'ALL' ? 'All models visible' : 'Model filter active'}</small>
            </div>
            <div className="summary-icon">M</div>
          </div>
        </section>

        <div className={chartMode ? 'scanner-workspace chart-open' : 'scanner-workspace'}>
          <section className="scanner-card">
          <div className="toolbar">
            <div className="search-wrap">
              <span className="search-icon">⌕</span>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search symbol, company or market..."
              />
            </div>

            <label className="primary-model-filter">
              <span>Model</span>
              <select value={model} onChange={(e) => setModel(e.target.value)}>
                <option value="ALL">All models</option>
                {models.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <div className="basic-filters">
              {BASIC_FILTERS.map((item) => (
                <button
                  key={item.key}
                  className={basicFilter === item.key ? `active ${item.key}` : item.key}
                  onClick={() => setBasicFilter(item.key)}
                >
                  {item.label}
                  <span>{counts[item.key]}</span>
                </button>
              ))}
            </div>

            <button
              className={`chart-toggle-button ${chartMode ? 'active' : ''}`}
              onClick={() => {
                setChartMode((value) => {
                  const next = !value;
                  if (!value && !selectedChartRecordId && sortedRecords.length > 0) {
                    setSelectedChartRecordId(sortedRecords[0].id);
                  }
                  return next;
                });
              }}
            >
              <span className="chart-icon">▣</span>
              {chartMode ? 'Close chart' : 'Chart'}
            </button>

            <button
              className={`advanced-button ${showAdvanced ? 'active' : ''}`}
              onClick={() => setShowAdvanced((value) => !value)}
            >
              <span className="filter-icon">≡</span>
              Advanced filters
            </button>
          </div>

          <div className="category-filter-row">
            <div className="category-filter-label">
              <span>Markets</span>
              <small>Choose one or multiple asset groups</small>
            </div>

            <div className="category-pills">
              <button
                className={allCategoriesSelected ? 'active all-markets' : 'all-markets'}
                onClick={selectAllCategories}
              >
                <i className="category-dot all" />
                All markets
                <span>{records.length}</span>
              </button>

              {CATEGORY_OPTIONS.map((item) => {
                const active = selectedCategories.includes(item.key);

                return (
                  <button
                    key={item.key}
                    className={active ? `active market-${item.key.toLowerCase()}` : `market-${item.key.toLowerCase()}`}
                    onClick={() => toggleCategory(item.key)}
                  >
                    <i className={`category-dot ${item.key.toLowerCase()}`} />
                    {item.label}
                    <span>{categoryCounts[item.key] || 0}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {showAdvanced && (
            <>
              <div className="advanced-panel advanced-market-panel">
                <div className="advanced-general-grid">
                  <label>
                    <span>Direction</span>
                    <select
                      value={direction}
                      onChange={(e) => setDirection(e.target.value)}
                    >
                      <option value="ALL">All</option>
                      <option value="BULLISH">Bullish</option>
                      <option value="BEARISH">Bearish</option>
                    </select>
                  </label>

                  <label>
                    <span>Max spread / entry ATR</span>
                    <select
                      value={maxSpreadAtr}
                      onChange={(e) => setMaxSpreadAtr(e.target.value)}
                    >
                      <option value="ANY">Any</option>
                      <option value="0.5">Less than 0.50 ATR</option>
                      <option value="1">Less than 1.00 ATR</option>
                      <option value="1.5">Less than 1.50 ATR</option>
                      <option value="2">Less than 2.00 ATR</option>
                    </select>
                  </label>

                  <button
                    className="clear-button"
                    onClick={() => {
                      setModel('ALL');
                      setDirection('ALL');
                      setContext1MinQuality('ANY');
                      setContext1MaxLeg('');
                      setContext2MinQuality('ANY');
                      setContext2MaxLeg('');
                      setMiddleMinQuality('ANY');
                      setMiddleMaxLeg('');
                      setMaxSpreadAtr('ANY');
                    }}
                  >
                    Clear market filters
                  </button>
                </div>

                <div className="timeframe-filter-heading">
                  <div>
                    <strong>Timeframe quality & leg filters</strong>
                    <small>
                      {showContext2
                        ? 'Filter Context 1, Context 2 and Middle independently using the v4.33 scanner metrics.'
                        : 'Filter Context 1 and Middle independently using the v4.33 scanner metrics.'}
                    </small>
                  </div>
                </div>

                <div className={`timeframe-filter-grid ${showContext2 ? 'with-context2' : 'without-context2'}`}> 
                  <div className="timeframe-filter-card context1">
                    <div className="timeframe-filter-title">
                      <span>C1</span>
                      <div>
                        <strong>Context 1</strong>
                        <small>Primary context timeframe</small>
                      </div>
                    </div>

                    <div className="timeframe-filter-controls">
                      <label>
                        <span>Minimum quality</span>
                        <select
                          value={context1MinQuality}
                          onChange={(e) => setContext1MinQuality(e.target.value)}
                        >
                          <option value="ANY">Any</option>
                          <option value="MEDIUM">Medium+</option>
                          <option value="GOOD">Good+</option>
                          <option value="BEST">Best</option>
                        </select>
                      </label>

                      <label>
                        <span>Maximum leg</span>
                        <input
                          type="number"
                          min="1"
                          value={context1MaxLeg}
                          onChange={(e) => setContext1MaxLeg(e.target.value)}
                          placeholder="Any"
                        />
                      </label>
                    </div>
                  </div>

                  {showContext2 && (
                  <div className="timeframe-filter-card context2">
                    <div className="timeframe-filter-title">
                      <span>C2</span>
                      <div>
                        <strong>Context 2</strong>
                        <small>Only models with a second context</small>
                      </div>
                    </div>

                    <div className="timeframe-filter-controls">
                      <label>
                        <span>Minimum quality</span>
                        <select
                          value={context2MinQuality}
                          onChange={(e) => setContext2MinQuality(e.target.value)}
                        >
                          <option value="ANY">Any</option>
                          <option value="MEDIUM">Medium+</option>
                          <option value="GOOD">Good+</option>
                          <option value="BEST">Best</option>
                        </select>
                      </label>

                      <label>
                        <span>Maximum leg</span>
                        <input
                          type="number"
                          min="1"
                          value={context2MaxLeg}
                          onChange={(e) => setContext2MaxLeg(e.target.value)}
                          placeholder="Any"
                        />
                      </label>
                    </div>
                  </div>

                  )}

                  <div className="timeframe-filter-card middle">
                    <div className="timeframe-filter-title">
                      <span>M</span>
                      <div>
                        <strong>Middle</strong>
                        <small>Opportunity / continuation timeframe</small>
                      </div>
                    </div>

                    <div className="timeframe-filter-controls">
                      <label>
                        <span>Minimum quality</span>
                        <select
                          value={middleMinQuality}
                          onChange={(e) => setMiddleMinQuality(e.target.value)}
                        >
                          <option value="ANY">Any</option>
                          <option value="MEDIUM">Medium+</option>
                          <option value="GOOD">Good+</option>
                          <option value="BEST">Best</option>
                        </select>
                      </label>

                      <label>
                        <span>Maximum leg</span>
                        <input
                          type="number"
                          min="1"
                          value={middleMaxLeg}
                          onChange={(e) => setMiddleMaxLeg(e.target.value)}
                          placeholder="Any"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="stock-filter-section">
                <div className="stock-filter-header">
                  <div>
                    <span className="stock-filter-kicker">STOCKS</span>
                    <strong>Stock momentum & volatility filters</strong>
                    <small>
                      Directional momentum follows the PAI setup direction. Activating
                      any stock filter restricts the result list to Stocks.
                    </small>
                  </div>

                  <button
                    className="clear-button stock-clear-button"
                    onClick={() => {
                      setStockExchange('ALL');
                      setStockPriceFilter('ANY');
                      setStockCustomMinPrice('');
                      setStockMin1D('');
                      setStockMin1W('');
                      setStockMin1M('');
                      setStockMinRange30('');
                      setStockMinAdr20('');
                      setStockMin2H('');
                      setStockMin8H('');
                    }}
                  >
                    Clear stock filters
                  </button>
                </div>

                <div className="stock-filter-grid">
                  <label>
                    <span>Stock market</span>
                    <select
                      value={stockExchange}
                      onChange={(e) => setStockExchange(e.target.value)}
                    >
                      <option value="ALL">All markets</option>
                      {stockExchangeOptions.map((exchange) => (
                        <option key={exchange.value} value={exchange.value}>
                          {exchange.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>Minimum stock price</span>
                    <select
                      value={stockPriceFilter}
                      onChange={(e) => setStockPriceFilter(e.target.value)}
                    >
                      <option value="ANY">Any</option>
                      <option value="5">≥ $5</option>
                      <option value="8">≥ $8</option>
                      <option value="CUSTOM">Custom</option>
                    </select>
                  </label>

                  {stockPriceFilter === 'CUSTOM' && (
                    <label>
                      <span>Custom min price $</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={stockCustomMinPrice}
                        onChange={(e) => setStockCustomMinPrice(e.target.value)}
                        placeholder="e.g. 12"
                      />
                    </label>
                  )}

                  <label>
                    <span>Min 1D momentum %</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={stockMin1D}
                      onChange={(e) => setStockMin1D(e.target.value)}
                      placeholder="Any"
                    />
                  </label>

                  <label>
                    <span>Min 1W momentum %</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={stockMin1W}
                      onChange={(e) => setStockMin1W(e.target.value)}
                      placeholder="Any"
                    />
                  </label>

                  <label>
                    <span>Min 1M momentum %</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={stockMin1M}
                      onChange={(e) => setStockMin1M(e.target.value)}
                      placeholder="Any"
                    />
                  </label>

                  <label>
                    <span>Min 30D range %</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={stockMinRange30}
                      onChange={(e) => setStockMinRange30(e.target.value)}
                      placeholder="Any"
                    />
                  </label>

                  <label>
                    <span>Min ADR(20) %</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={stockMinAdr20}
                      onChange={(e) => setStockMinAdr20(e.target.value)}
                      placeholder="Any"
                    />
                  </label>

                  <label>
                    <span>Min 2H impulse %</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={stockMin2H}
                      onChange={(e) => setStockMin2H(e.target.value)}
                      placeholder="Any"
                    />
                  </label>

                  <label>
                    <span>Min 8H impulse %</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={stockMin8H}
                      onChange={(e) => setStockMin8H(e.target.value)}
                      placeholder="Any"
                    />
                  </label>
                </div>

                <div className="stock-sort-panel">
                  <div className="stock-sort-heading">
                    <div>
                      <strong>Sort stock results</strong>
                      <small>
                        Sorting happens after all filters. Momentum sorting follows the
                        PAI direction, so bearish negative moves are ranked by bearish
                        strength.
                      </small>
                    </div>

                    {stockSortActive && (
                      <button
                        className="stock-sort-reset"
                        onClick={() => chooseStockSort('DEFAULT')}
                      >
                        Reset sorting
                      </button>
                    )}
                  </div>

                  <div className="stock-sort-buttons">
                    {STOCK_SORT_OPTIONS.map((option) => (
                      <button
                        key={option.key}
                        className={
                          stockSortKey === option.key
                            ? 'stock-sort-button active'
                            : 'stock-sort-button'
                        }
                        onClick={() => chooseStockSort(option.key)}
                        title={
                          stockSortKey === option.key
                            ? 'Click again to reverse the order'
                            : `Sort by ${option.label}`
                        }
                      >
                        <span>{option.label}</span>
                        <strong>{stockSortArrow(option.key)}</strong>
                      </button>
                    ))}
                  </div>

                  {stockSortActive && activeStockSort && (
                    <div className="stock-sort-active-note">
                      Sorted by <strong>{activeStockSort.label}</strong> —{' '}
                      {stockSortMeaning}. Click the active sort again to reverse it.
                    </div>
                  )}
                </div>

                {stockViewActive && (
                  <div className="stock-filter-active-note">
                    {stockFiltersActive && stockSortActive
                      ? 'Stock filters and sorting active — non-stock markets are temporarily hidden.'
                      : stockFiltersActive
                        ? 'Stock filters active — non-stock markets are temporarily hidden.'
                        : 'Stock sorting active — non-stock markets are temporarily hidden.'}
                  </div>
                )}
              </div>
            </>
          )}

          {apiError && (
            <div className="notice warning">
              <strong>Waiting for scanner data.</strong>
              <span>{apiError}</span>
            </div>
          )}

          {!apiError && stale && (
            <div className="notice warning">
              <strong>Snapshot looks stale.</strong>
              <span>
                Last MT5 export is {snapshotAge}s old. The last valid data is
                still displayed.
              </span>
            </div>
          )}

          <div className="table-meta">
            <span>
              Showing <strong>{sortedRecords.length}</strong> of{' '}
              <strong>{records.length}</strong> model records
            </span>

            <div className="table-meta-right">
              {stockSortActive && activeStockSort && (
                <span className="table-sort-indicator">
                  Sort: {activeStockSort.label}{' '}
                  {stockSortDirection === 'desc' ? '↓' : '↑'}
                </span>
              )}

              <span>
                Scan:{' '}
                {data?.snapshot?.scan?.durationMs !== undefined
                  ? `${data.snapshot.scan.durationMs} ms`
                  : '—'}
              </span>
            </div>
          </div>

          <div className="table-scroll">
            <table className={showContext2 ? 'has-context2' : 'single-context'}>
              <thead>
                <tr className="table-group-header">
                  <th rowSpan="2" className="symbol-column">Symbol</th>
                  <th rowSpan="2">Market</th>
                  <th rowSpan="2">Model</th>
                  <th rowSpan="2">Direction</th>
                  <th colSpan="3" className="tf-group tf-group-context1">
                    Context 1
                  </th>
                  {showContext2 && (
                    <th colSpan="3" className="tf-group tf-group-context2">
                      Context 2
                    </th>
                  )}
                  <th colSpan="3" className="tf-group tf-group-middle">
                    Middle
                  </th>
                  <th rowSpan="2" className="spread-column">Spread / ATR</th>
                  <th rowSpan="2" className="stock-metrics-header stock-column">
                    Stock Momentum <span>sortable</span>
                  </th>
                  <th rowSpan="2" className="status-column">Status</th>
                </tr>
                <tr className="table-subheader">
                  <th className="tf-sub tf-group-context1">Phase</th>
                  <th className="tf-sub tf-group-context1">Quality</th>
                  <th className="tf-sub tf-group-context1">Leg</th>

                  {showContext2 && (
                    <>
                      <th className="tf-sub tf-group-context2">Phase</th>
                      <th className="tf-sub tf-group-context2">Quality</th>
                      <th className="tf-sub tf-group-context2">Leg</th>
                    </>
                  )}

                  <th className="tf-sub tf-group-middle">Phase</th>
                  <th className="tf-sub tf-group-middle">Quality</th>
                  <th className="tf-sub tf-group-middle">Leg</th>
                </tr>
              </thead>
              <tbody>
                {sortedRecords.map((record) => (
                  <tr
                    key={record.id}
                    className={`${rowClass(record)} ${chartMode ? 'chart-selectable-row' : ''} ${selectedChartRecordId === record.id ? 'chart-row-active' : ''}`.trim()}
                    onClick={() => {
                      if (chartMode) setSelectedChartRecordId(record.id);
                    }}
                  >
                    <td className="symbol-column">
                      <InstrumentSymbolCell record={record} />
                    </td>
                    <td>
                      <span className={`category-tag ${String(
                        record?.category || 'OTHER'
                      ).toLowerCase()}`}>
                        {CATEGORY_OPTIONS.find(
                          (item) => item.key === (record?.category || 'OTHER')
                        )?.label || 'Other'}
                      </span>
                    </td>
                    <td>
                      <span className="model-pill">
                        <i />
                        {record.model}
                      </span>
                    </td>
                    <td>
                      <span
                        className={directionClass(
                          record?.overall?.directionCode
                        )}
                      >
                        {record?.overall?.direction || 'NONE'}
                      </span>
                    </td>
                    <td className="tf-data tf-data-context1">
                      <TimeframePhaseCell record={record} metricKey="context1" />
                    </td>
                    <td className="tf-data tf-data-context1">
                      <TimeframeQualityCell record={record} metricKey="context1" />
                    </td>
                    <td className="tf-data tf-data-context1 tf-leg-column">
                      <TimeframeLegCell record={record} metricKey="context1" />
                    </td>

                    {showContext2 && (
                      <>
                        <td className="tf-data tf-data-context2">
                          <TimeframePhaseCell record={record} metricKey="context2" />
                        </td>
                        <td className="tf-data tf-data-context2">
                          <TimeframeQualityCell record={record} metricKey="context2" />
                        </td>
                        <td className="tf-data tf-data-context2 tf-leg-column">
                          <TimeframeLegCell record={record} metricKey="context2" />
                        </td>
                      </>
                    )}

                    <td className="tf-data tf-data-middle">
                      <TimeframePhaseCell record={record} metricKey="middle" />
                    </td>
                    <td className="tf-data tf-data-middle">
                      <TimeframeQualityCell record={record} metricKey="middle" />
                    </td>
                    <td className="tf-data tf-data-middle tf-leg-column">
                      <TimeframeLegCell record={record} metricKey="middle" />
                    </td>
                    <td className="spread-column">
                      <SpreadMetricsCell record={record} />
                    </td>
                    <td className="stock-column">
                      <StockMetricsCell record={record} />
                    </td>
                    <td className="status-column">
                      <div className="status-cell">
                        <StatusPill record={record} />
                        <span>{record?.overall?.status || '—'}</span>
                      </div>
                    </td>
                  </tr>
                ))}

                {!loading && sortedRecords.length === 0 && (
                  <tr>
                    <td colSpan={showContext2 ? 16 : 13}>
                      <div className="empty-state">
                        <strong>No records match these filters.</strong>
                        <span>
                          Adjust the filters or wait for the next MT5 snapshot.
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </section>

          {chartMode && (
            <aside className="chart-pane">
              {selectedChartRecord ? (
                <TradingViewChartPanel record={selectedChartRecord} />
              ) : (
                <div className="chart-card">
                  <div className="chart-placeholder">
                    <strong>No chart selected.</strong>
                    <span>Choose a row from the scanner to load its TradingView chart.</span>
                  </div>
                </div>
              )}
            </aside>
          )}
        </div>
      </main>
    </div>
  );
}

export default App;
